import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Computer, Cloud, Users, Cpu, 
  Network, Globe, Grid, MoreHorizontal 
} from 'lucide-react';
import { Base_Url } from '../config';
import { useParams, useNavigate } from 'react-router-dom';



const AddAdvancedServiceCard = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [serviceData, setServiceData] = useState({
    title: '',
    items: [{ name: '', link: '',}],
    externalLink: '',
    parentpath: '',
    card_desc:'',
    cardpath: '',
    icon: '',
    author:'',
    isHighlight: false,
    description: '' 
  });

  const [existingRoutes, setExistingRoutes] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);
  const [parentRoutes, setParentRoutes] = useState([]);
  const [searchTerms, setSearchTerms] = useState({
    parentpath: '',
    cardpath: '',
    items: Array(serviceData.items.length).fill('')
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState({
    parentpath: false,
    cardpath: false,
    items: []
  });

  const dropdownRefs = useRef({
    parentpath: null,
    cardpath: null,
    items: []
  });


  // Fetch all routes
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await axios.get(`${Base_Url}/createroutes`);
        setAllRoutes(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to fetch routes', err);
        setAllRoutes([]);
      }
    };
    fetchRoutes();
  }, []);

  // Fetch parent routes specific to the current ID
  useEffect(() => {
    const fetchParentRoutes = async () => {
      try {
        const response = await axios.get(`${Base_Url}/createroutes/${id}`);
        
        // If parent routes exist, pre-fill the parent path
        if (response.data && response.data.routes) {
          setServiceData(prev => ({
            ...prev,
            parentpath: response.data.routes
          }));
          setParentRoutes(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch parent routes', err);
        setParentRoutes([]);
      }
    };
    
    if (id) {
      fetchParentRoutes();
    }
  }, [id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check parent path dropdown
      if (dropdownRefs.current.parentpath && 
          !dropdownRefs.current.parentpath.contains(event.target)) {
        setIsDropdownOpen(prev => ({
          ...prev,
          parentpath: false
        }));
        setSearchTerms(prev => ({
          ...prev,
          parentpath: ''
        }));
      }

      // Check card path dropdown
      if (dropdownRefs.current.cardpath && 
          !dropdownRefs.current.cardpath.contains(event.target)) {
        setIsDropdownOpen(prev => ({
          ...prev,
          cardpath: false
        }));
        setSearchTerms(prev => ({
          ...prev,
          cardpath: ''
        }));
      }

      // Check items dropdowns
      dropdownRefs.current.items.forEach((ref, index) => {
        if (ref && !ref.contains(event.target)) {
          setIsDropdownOpen(prev => {
            const newItemDropdowns = [...prev.items];
            newItemDropdowns[index] = false;
            return { ...prev, items: newItemDropdowns };
          });
          
          setSearchTerms(prev => {
            const newItemSearchTerms = [...prev.items];
            newItemSearchTerms[index] = '';
            return { ...prev, items: newItemSearchTerms };
          });
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [serviceData.items.length]);

  const addItem = () => {
    setServiceData(prevState => {
      const newItems = [...prevState.items, { name: '', link: '' }];
      
      setSearchTerms(prev => ({
        ...prev,
        items: [...prev.items, '']
      }));

      setIsDropdownOpen(prev => ({
        ...prev,
        items: [...prev.items, false]
      }));

      return {
        ...prevState,
        items: newItems
      };
    });
  };

  const removeItem = (index) => {
    setServiceData(prevState => {
      const newItems = prevState.items.filter((_, i) => i !== index);
      
      const newSearchTerms = searchTerms.items.filter((_, i) => i !== index);
      const newDropdownState = isDropdownOpen.items.filter((_, i) => i !== index);

      setSearchTerms(prev => ({
        ...prev,
        items: newSearchTerms
      }));

      setIsDropdownOpen(prev => ({
        ...prev,
        items: newDropdownState
      }));

      return {
        ...prevState,
        items: newItems
      };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setServiceData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // For route-based inputs, update search terms and open dropdown
    if (['parentpath', 'cardpath'].includes(name)) {
      setSearchTerms(prev => ({
        ...prev,
        [name]: value
      }));
      setIsDropdownOpen(prev => ({
        ...prev,
        [name]: true
      }));
    }
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    setServiceData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [name]: value };
      return { ...prev, items: newItems };
    });

    // Update search terms for items
    if (name === 'link') {
      setSearchTerms(prev => {
        const newItemSearchTerms = [...prev.items];
        newItemSearchTerms[index] = value;
        return { ...prev, items: newItemSearchTerms };
      });

      // Open dropdown for specific item
      setIsDropdownOpen(prev => {
        const newItemDropdowns = [...prev.items];
        newItemDropdowns[index] = true;
        return { ...prev, items: newItemDropdowns };
      });
    }
  };

  const getAvailableRoutes = (type, index = null) => {
    // Collect all currently selected routes
    const selectedRoutes = [
      serviceData.parentpath, 
      serviceData.cardpath, 
      ...serviceData.items.map(item => item.link)
    ].filter(route => route !== '');

    // Filter out already selected routes
    const availableRoutes = allRoutes.filter(route => 
      !selectedRoutes.includes(route.routes)
    );

    // If searching, further filter by search term
    let filteredRoutes = availableRoutes;
    if (type === 'parentpath' && searchTerms.parentpath) {
      filteredRoutes = filteredRoutes.filter(route => 
        route.routes.toLowerCase().includes(searchTerms.parentpath.toLowerCase())
      );
    } else if (type === 'cardpath' && searchTerms.cardpath) {
      filteredRoutes = filteredRoutes.filter(route => 
        route.routes.toLowerCase().includes(searchTerms.cardpath.toLowerCase())
      );
    } else if (type === 'itemLink' && index !== null && searchTerms.items[index]) {
      filteredRoutes = filteredRoutes.filter(route => 
        route.routes.toLowerCase().includes(searchTerms.items[index].toLowerCase())
      );
    }

    return filteredRoutes.slice(0, 5);
  };

  const handleRouteSelect = (route, type, itemIndex = null) => {
    if (type === 'parentpath') {
      setServiceData(prev => ({
        ...prev,
        parentpath: route.routes
      }));
      setSearchTerms(prev => ({
        ...prev,
        parentpath: ''
      }));
      setIsDropdownOpen(prev => ({
        ...prev,
        parentpath: false
      }));
    } else if (type === 'cardpath') {
      setServiceData(prev => ({
        ...prev,
        cardpath: route.routes
      }));
      setSearchTerms(prev => ({
        ...prev,
        cardpath: ''
      }));
      setIsDropdownOpen(prev => ({
        ...prev,
        cardpath: false
      }));
    } else if (type === 'itemLink' && itemIndex !== null) {
      setServiceData(prev => {
        const newItems = [...prev.items];
        newItems[itemIndex].link = route.routes;
        return { ...prev, items: newItems };
      });
      setSearchTerms(prev => {
        const newItemSearchTerms = [...prev.items];
        newItemSearchTerms[itemIndex] = '';
        return { ...prev, items: newItemSearchTerms };
      });
      setIsDropdownOpen(prev => {
        const newItemDropdowns = [...prev.items];
        newItemDropdowns[itemIndex] = false;
        return { ...prev, items: newItemDropdowns };
      });
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
  
    // Comprehensive Validation
    const validationErrors = [];
  
    // Title Validation
    if (!serviceData.title || serviceData.title.trim() === '') {
      validationErrors.push('Card Title is required');
    }
  
    // Parent Path Validation
    if (!serviceData.parentpath || serviceData.parentpath.trim() === '') {
      validationErrors.push('Parent Route is required');
    }
  
    // Card Path Validation
    // if (!serviceData.cardpath || serviceData.cardpath.trim() === '') {
    //   validationErrors.push('Card Route is required');
    // }
  
    // Items Validation
    // if (!serviceData.items || serviceData.items.length === 0) {
    //   validationErrors.push('At least one service item is required');
    // } else {
    //   serviceData.items.forEach((item, index) => {
    //     if (!item.name || item.name.trim() === '') {
    //       validationErrors.push(`Item ${index + 1}: Name is required`);
    //     }
    //     if (!item.link || item.link.trim() === '') {
    //       validationErrors.push(`Item ${index + 1}: Link is required`);
    //     }
    //   });
    // }


    if (serviceData.items && serviceData.items.length > 0) {
      serviceData.items.forEach((item, index) => {
        // If either name or link is provided, both must be filled
        if ((item.name && !item.link) || (!item.name && item.link)) {
          validationErrors.push(`Item ${index + 1}: Both Name and Link must be provided if one is filled`);
        }
      });
    }
  
    // If there are validation errors, show them and stop submission
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }
  
    try {
      // Prepare data for submission
      const submissionData = {
        card_details: {
          ...serviceData,
          // Trim all string values to remove extra whitespace
          title: serviceData.title.trim(),
          parentpath: serviceData.parentpath.trim(),
          cardpath: serviceData.cardpath.trim(),
          externalLink: serviceData.externalLink ? serviceData.externalLink.trim() : '',
          card_desc: serviceData.card_desc || null  ,
          items: serviceData.items.map(item => ({
            name: item.name.trim(),
            link: item.link.trim()
          }))
        },
        route_id: id || null , 
        author: serviceData.author || null  ,
      };
  
      // Submission with axios
      const response = await axios.post(`${Base_Url}/cardsinfo`, submissionData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      // Check response status
      if (response.status === 200 || response.status === 201) {
        // Success handling
        setSuccess('Card added successfully!');
        
        // Optional: Reset form or navigate
        setServiceData({
          title: '',
          items: [{ name: '', link: '' }],
          externalLink: '',
          parentpath: '',
          cardpath: '',
          author:'',
          icon: 'Computer',
          isHighlight: false,
          description: ''
        });
  
        // Soft refresh or navigation
        window.location.reload();
        // Alternatively: navigate('/your-redirect-path');
      } else {
        // Unexpected response
        setError('Unexpected response from server');
      }
  
    } catch (err) {
      // Comprehensive error handling
      console.error('Submission Error:', err);
  
      let errorMessage = 'Failed to add service';
  
      if (err.response) {
        // The request was made and the server responded with a status code
        console.error('Error Response Data:', err.response.data);
        console.error('Error Response Status:', err.response.status);
        
        errorMessage = err.response.data.message || 
                       err.response.data.error || 
                       `Server error: ${err.response.status}`;
      } else if (err.request) {
        // The request was made but no response was received
        console.error('No response received:', err.request);
        errorMessage = 'No response from server. Please check your network connection.';
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', err.message);
        errorMessage = `Request setup error: ${err.message}`;
      }
  
      setError(errorMessage);
    }
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h2 className="text-center">Add Card</h2>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              {success && (
                <div className="alert alert-success" role="alert">
                  {success}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Card Title</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="title"
                    placeholder='Your Card Name'
                    value={serviceData.title}
                    onChange={handleChange}
                    required 
                  />
                </div>

                <div className="mb-3">
                <label className="form-label">Service Items</label>
                {serviceData.items.map((item, index) => (
                  <div key={index} className="row mb-2 align-items-center position-relative">
                    <div className="col-md-5">
                      <input 
                        type="text" 
                        className="form-control" 
                        name="name"
                        placeholder="Item Name"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, e)}
                        // required 
                      />
                    </div>
                    <div 
                      className="col-md-5 position-relative"
                      ref={(el) => {
                        if (dropdownRefs.current.items.length < serviceData.items.length) {
                          dropdownRefs.current.items.push(el);
                        }
                      }}
                    >
                      <input 
                        type="text" 
                        className="form-control" 
                        name="link"
                        placeholder="Item Link"
                        value={item.link}
                        onChange={(e) => handleItemChange(index, e)}
                        autoComplete="off"
                      />
                      {isDropdownOpen.items[index] && searchTerms.items[index] !== undefined && (
                        <div 
                          className="position-absolute bg-white border rounded w-100 shadow-sm" 
                          style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                        >
                          {getAvailableRoutes('itemLink', index).map((route, routeIndex) => (
                            <div 
                              key={route.id || routeIndex} 
                              className="dropdown-item p-2 hover-bg-light"
                              onClick={() => handleRouteSelect(route, 'itemLink', index)}
                              style={{ cursor: 'pointer' }}
                            >
                              {route.routes}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="col-md-2">
                      {index > 0 && (
                        <button 
                          type="button" 
                          className="btn btn-danger" 
                          onClick={() => removeItem(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button 
                  type="button" 
                  className="btn btn-secondary mt-2" 
                  onClick={addItem}
                >
                  Add Item
                </button>
              </div>

                <div className="mb-3">
                  <label className="form-label">External Link (Optional)</label>
                  <input 
                    type="url" 
                    className="form-control" 
                    name="externalLink"
                    value={serviceData.externalLink}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Card Description(Optional)</label>
                  <textarea 
                    type="url" 
                    className="form-control" 
                    name="card_desc"
                    value={serviceData.card_desc}
                    onChange={handleChange}
                    placeholder="Enter Card Description"
                  />
                </div>

                <div className="mb-3 position-relative">
                  <label className="form-label">Main Parent Route</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="parentpath"
                    value={serviceData.parentpath}
                    onChange={handleChange}
                    placeholder="/parent/your-childroute"
                    required 
                    autoComplete="off"
                    readOnly={!!parentRoutes.routes} 
                  />
                  {isDropdownOpen.parentpath && searchTerms.parentpath !== '' && (
                    <div 
                      className="position-absolute bg-white border rounded w-100 shadow-sm" 
                      style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                      onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
                    >
                      {getAvailableRoutes('parentpath').map((route, routeIndex) => (
                        <div 
                          key={route.id || routeIndex} 
                          className="dropdown-item p-2 hover-bg-light"
                          onClick={() => handleRouteSelect(route, 'parentpath')}
                          style={{cursor: 'pointer'}}
                        >
                          {route.routes}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-3 position-relative">
                  <label className="form-label">Main Card Routes</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="cardpath"
                    value={serviceData.cardpath}
                    onChange={handleChange}
                    placeholder="/thiscardroute"
                    // required 
                    autoComplete="off"
                  />
                  {searchTerms.cardpath !== '' && (
                    <div 
                      className="position-absolute bg-white border rounded w-100 shadow-sm" 
                      style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                    >
                      {getAvailableRoutes('cardpath').map((route, routeIndex) => (
                        <div 
                          key={route.id || routeIndex} 
                          className="dropdown-item p-2 hover-bg-light"
                          onClick={() => handleRouteSelect(route, 'cardpath')}
                          style={{cursor: 'pointer'}}
                        >
                          {route.routes}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Author</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="author"
                    placeholder='Enter Author'
                    value={serviceData.author}
                    onChange={handleChange}
                    required 
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Icon</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="icon"
                    value={serviceData.icon}
                    onChange={handleChange}
                    placeholder="Follow this link to get icons: https://lucide.dev/icons/ and copy the JSX code, then paste it here."
                    // required 
                    autoComplete="off"
                  />
                <a href="https://lucide.dev/icons/" target="_blank">
  <button type="button" class="btn btn">
    <span class="badge badge-light"> Follow this link to get icons: https://lucide.dev/icons/ and copy the JSX code, then paste it here.</span>
  </button>
</a>

                </div>

                <div className="text-center">
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-lg"
                    // disabled={loading}
                  >
                   Add
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAdvancedServiceCard;